import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  alpha,
  Fade,
  Alert,
  Snackbar,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchConsultationHistory } from "../../features/consultations/consultationHistorySlice";
import ConsultationPreviewModal from "../../components/ConsultationPreviewModal";
import SearchIcon from "@mui/icons-material/Search";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const ConsultationHistory = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data, count, loading, error, next, previous } = useSelector((state) => state.consultationHistory);

  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [filterByDoctor, setFilterByDoctor] = useState("");
  const [filterByDate, setFilterByDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFiltering, setIsFiltering] = useState(false);

  // Fetch data on load & page change
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchConsultationHistory({ 
          page: page + 1, 
          rowsPerPage, 
          searchTerm, 
          filterByDoctor, 
          filterByDate 
        })).unwrap();
      } catch (error) {
        setErrorMessage('Unable to load consultation history. Please try again later.');
      }
    };
    fetchData();
  }, [dispatch, page, rowsPerPage, searchTerm, filterByDoctor, filterByDate]);

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  }, []);

  // Handle view details
  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedConsultation(null);
  };

  // Handle download
  const handleDownload = async (consultation) => {
    try {
      // Here you would typically implement the download functionality
      // For now, we'll just show a success message
      toast.success("Download started!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to download consultation details", {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  // Safe getters for nested data
  const safeGetDoctorName = (doctor) => (doctor ? doctor?.user.name || "No Name" : "Unknown");
  const safeGetPatientName = (patient) => (patient ? patient?.name || "No Name" : "Unknown");

  // Format date
  const formatDate = (date) => {
    try {
      return format(new Date(date), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" sx={{ marginBottom: 3, fontWeight: 600, textAlign: "center" }}>
        <EventNoteIcon sx={{ fontSize: 30, verticalAlign: "middle", mr: 1 }} />
        Consultation History
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Consultations
                  </Typography>
                  <EventNoteIcon color="primary" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {count || 0}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  <CheckCircleIcon color="success" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {data?.filter(c => c.is_completed).length || 0}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Follow-ups Required
                  </Typography>
                  <PendingIcon color="warning" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {data?.filter(c => c.follow_up_required).length || 0}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Active Patients
                  </Typography>
                  <PersonIcon color="info" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {new Set(data?.map(c => c.patient?.id)).size || 0}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Filters & Search */}
      <Grid container spacing={2} sx={{ marginBottom: 2, alignItems: "center" }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Search by Patient Name"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Filter by Doctor</InputLabel>
            <Select value={filterByDoctor} onChange={(e) => setFilterByDoctor(e.target.value)}>
              <MenuItem value="">All Doctors</MenuItem>
              {Array.from(new Set(data?.map((c) => safeGetDoctorName(c.doctor)))).map((doctor) => (
                <MenuItem key={doctor} value={doctor}>
                  {doctor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField 
            fullWidth 
            type="date" 
            label="Filter by Date" 
            InputLabelProps={{ shrink: true }} 
            value={filterByDate} 
            onChange={(e) => setFilterByDate(e.target.value)} 
          />
        </Grid>
        <Grid item xs={12} sm={1} sx={{ textAlign: "right" }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => dispatch(fetchConsultationHistory({ 
              page: page + 1, 
              rowsPerPage, 
              searchTerm, 
              filterByDoctor, 
              filterByDate 
            }))}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage('')} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Loading & Error Handling */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Consultation Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>
                      <LocalHospitalIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Doctor
                    </TableCell>
                    <TableCell>
                      <PersonIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Patient
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.map((consultation) => (
                    <TableRow 
                      key={consultation.id} 
                      hover 
                      sx={{ 
                        cursor: "pointer",
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell>{consultation.consultation_number}</TableCell>
                      <TableCell>{safeGetDoctorName(consultation.doctor)}</TableCell>
                      <TableCell>{safeGetPatientName(consultation.patient)}</TableCell>
                      <TableCell>{formatDate(consultation.consultation_date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {consultation.is_completed ? (
                            <Chip label="Completed" color="success" size="small" />
                          ) : (
                            <Chip label="Pending" color="warning" size="small" />
                          )}
                          {consultation.follow_up_required && (
                            <Chip label="Follow-up" color="info" size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton 
                              color="primary" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(consultation);
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton 
                              color="primary" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(consultation);
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Server-Side Pagination */}
            <TablePagination
              component="div"
              count={count || 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              nextButtonDisabled={!next}
              backButtonDisabled={!previous}
            />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Consultation Details Modal */}
      <ConsultationPreviewModal
        open={modalOpen}
        onClose={handleCloseModal}
        consultationData={selectedConsultation || {}}
      />
    </Box>
  );
};

export default ConsultationHistory;
